/**
 * 테스트월별통장내역 화면 기능정의서.
 * jangi manageFeatureSpecs 와 같은 형식.
 * 화면 「기능정의서」패널·가이드에 연결된다.
 */

export interface BankbookTestFeatureItem {
  name: string;
  description: string;
  /** 화면 data-tour 연결용 (튜토리얼 하이라이트) */
  target?: string;
  keywords?: string[];
}

export interface BankbookGuidePrepareResult {
  /** 스포트라이트용 대체 셀렉터 (모달 등) */
  spotSelectors?: string[];
  status?: string;
}

export interface BankbookGuideHost {
  /** 가이드 시작 시 통장 선택·테스트 자료 준비 (confirm 포함) */
  ensureData: () => Promise<{ ok: boolean; message?: string; cancelled?: boolean }>;
  /** 단계마다 탭 이동·모달 오픈 (사전 알림 포함) */
  prepare: (target: string) => Promise<BankbookGuidePrepareResult | void>;
  /** 가이드 종료 시 모달/오버레이 정리 */
  cleanup: () => void;
  /** 비차단 알림 */
  notify?: (message: string, tone?: 'info' | 'success' | 'warn') => void;
}

export interface BankbookTestFeatureSpec {
  title: string;
  summary: string;
  audience: 'admin';
  purpose: string;
  features: BankbookTestFeatureItem[];
  notes?: string[];
  keywords?: string[];
}

/** route name: memberBankbook / memberDetailBankbook (구 테스트 라우트는 리다이렉트) */
export const BANKBOOK_TEST_FEATURE_SPEC: BankbookTestFeatureSpec = {
  title: '테스트월별통장내역',
  summary:
    '은행 거래내역을 올리고 → 계정·자금원천을 맞추고 → 수납·영수증까지 정리한 뒤 → 마감하는 화면입니다. 처음이면 「자동으로 보기」를 눌러 보세요.',
  audience: 'admin',
  purpose:
    '이 달 통장내역을 빠르게 정리하는 화면입니다. 여러 건을 고른 뒤 오른쪽에서 한꺼번에 지정하고, 고른 항목에 저장된 기준을 넣으려면 「자동화 적용」을 누릅니다.',
  keywords: [
    '통장',
    '현금출납',
    '계정조정',
    '자금원천',
    '수납',
    '영수증',
    '분개',
    '마감',
    '본인부담금',
    '자동화',
    '추천',
  ],
  features: [
    {
      name: '기능정의서',
      description:
        '오른쪽 위 「기능정의서」. 처음이면 「처음부터 자동으로 보기」를 누르세요. 시작·샘플 만들기·종료는 물어보고, 화면이 직접 가리켜 줍니다.',
      keywords: ['기능정의서', '도움말', '매뉴얼', '가이드'],
    },
    {
      name: '연월 선택',
      description:
        '상단에서 이전/다음 달로 이동하거나 월 선택기로 연월을 바꾼다. 시설 다른 화면과 같은 연월 저장소를 쓴다.',
      target: 'bb-test-ym',
      keywords: ['연월', '달', '기간'],
    },
    {
      name: '통장 선택',
      description:
        '전체 또는 개별 통장을 고른다. 마감된 통장은 자물쇠로 표시된다. 톱니 버튼으로 통장 등록·수정·삭제를 한다.',
      target: 'bb-test-banks',
      keywords: ['통장', '계좌', '전체'],
    },
    {
      name: '통장 관리',
      description:
        '상단 통장 칩 옆 톱니로 연다. 별칭·은행·계좌·사업구분·사용기간으로 등록·수정하고, 사용중/종료됨 목록에서 삭제한다. ?로 사업비·보조비·후원금·비급여 안내를 본다.',
      target: 'bb-test-bank-manage',
      keywords: ['통장관리', '계좌', '등록'],
    },
    {
      name: '이 달 마감 · 마감 해제',
      description:
        '통장 하나 선택 후 「이 달 마감」→ 미조정 검사 → 통과 시 마감 처리. 마감 후 수정·삭제·업로드 불가. 더보기에서 마감 해제(희망이음 요청 중이면 불가, 업로드 완료면 경고).',
      target: 'bb-test-closing',
      keywords: ['마감', '확정', '해제'],
    },
    {
      name: '더보기 (마감 해제 · 테스트자료)',
      description:
        '마감 해제, (최고관리자) 테스트 자료 생성. 기억된 적요·계정·수입–지출 연결은 우측 계정 탭 톱니(추천 기준)에서 연다.',
      target: 'bb-test-more',
      keywords: ['마감해제', '테스트'],
    },
    {
      name: '작업 단계 1~4',
      description:
        '1 통장내역 → 2 수납 → 3 영수증 → 4 계정별내역. 통장 데이터가 없으면 2~4는 잠긴다. 미완료 건이 있으면 단계에 표시된다.',
      target: 'bb-test-steps',
      keywords: ['단계', '탭', '워크플로'],
    },

    /* —— 1. 통장내역 —— */
    {
      name: '좌측 요약',
      description: '이월금·수입·지출·잔액을 본다. 현재 통장·조건이 반영된 합계다.',
      target: 'bb-test-summary',
      keywords: ['이월', '잔액', '합계'],
    },
    {
      name: '인건비비율 · 상세',
      description:
        '카드에서 누적 비율·충족 여부를 보고, 클릭 시 산식·월별 추이·수입/지출 구성을 조회한다. 조회 전용.',
      target: 'bb-test-labor',
      keywords: ['인건비', '비율'],
    },
    {
      name: '조건 · 보기',
      description:
        '현금출납부(분개 펼침) / 통장 원본(분개 묶음), 전체·분개만, 일자·계정·적요로 목록을 좁힌다. 「초기화」로 조건을 비운다.',
      target: 'bb-test-conditions',
      keywords: ['필터', '검색', '분개만', '원본'],
    },
    {
      name: '업로드 · 엑셀',
      description:
        '통장별 엑셀을 고른 뒤 헤더·적요 컬럼을 확인하고 업로드한다. 계정은 통장내역에서 지정. 마감 시 불가.',
      target: 'bb-test-upload-quick',
      keywords: ['엑셀', '업로드'],
    },
    {
      name: '업로드 · 직접 입력',
      description:
        '수입/지출·통장·일자·금액·적요를 손으로 넣는다. 같은 날 기존 건이 있으면 삽입 위치를 고른다.',
      target: 'bb-test-upload-manual',
      keywords: ['직접입력', '항목추가'],
    },
    {
      name: '결산',
      description:
        '세입·세출 결산표(예산/결산/증감, 관항목·보조금·합계). 조회 전용이며 저장 버튼은 없다.',
      target: 'bb-test-settlement',
      keywords: ['결산', '세입', '세출'],
    },
    {
      name: '설정 (톱니)',
      description:
        '계정·상대계정·자금원천의 「자동화 적용」은 같은 창입니다. 계정은 계정을 고른 뒤 적요를 넣고, 상대계정은 수입 계정을 고른 뒤 지출 계정을 고릅니다. 계정과 자금원천은 수입/지출로 나뉘고, 상대계정은 지출만 보여 줍니다. 이 기관에 저장된 것이 없으면 추천이 기본으로 나옵니다. 자금원천은 비어 있는 계정을 추천으로 채웁니다. 목록의 연필로 고칠 수 있습니다. 저장을 누르면 이 기관 것이 됩니다. 지금 연 항목이 저장된 다른 기관에서 가져올 수도 있습니다. 계정은 없는 적요만 더하고, 상대계정과 자금원천은 없는 것만 가져오거나 이 기관 것을 지우고 전부 바꿀 수 있습니다. 통장에서 고른 항목이 있으면 그 항목만 적용합니다. 고른 항목이 없으면 이번 달 전체에 적용합니다. 「다음」을 누르면 빈 곳만 채울지, 이미 넣은 값까지 바꿀지 고릅니다. 「적용하기」를 눌러야 통장이 바뀌고, 적용됐는지 알림으로 알려 줍니다.',
      target: 'bb-test-recommend-settings',
      keywords: ['자동화', '미리보기', '계정자동', '자금원천', '본인부담금', '일괄'],
    },
    {
      name: '모두삭제',
      description:
        '현재 조건에 보이는 통장내역을 일괄 삭제한다. 마감·확정 상태에서는 나오지 않는다. 되돌릴 수 없다.',
      target: 'bb-test-delete-all',
      keywords: ['삭제', '일괄'],
    },
    {
      name: '희망이음 업로드',
      description:
        '마감 후 통장 단위로 희망이음 업로드를 요청·취소한다. 요청 중에는 마감 해제가 막힌다.',
      target: 'bb-test-heumieum',
      keywords: ['희망이음', 'CES'],
    },
    {
      name: '이달의 메모',
      description: '사이드바 하단에서 해당 기관·연월 메모를 적고 저장한다.',
      target: 'bb-test-memo',
      keywords: ['메모'],
    },
    {
      name: '공유원장 · 선택',
      description:
        '수입/지출 탭, 행·헤더 체크(모두선택), 통장원본 분개 묶음 펼치기. 여러 건 선택 후 우측에서 일괄 지정한다.',
      target: 'bb-test-ledger',
      keywords: ['선택', '모두선택', '체크', '원장'],
    },
    {
      name: '수급자 열 (수입 112)',
      description:
        '본인부담금 행에서 수급자를 지정·변경·해제하거나 「수납 탭에서 보기」로 이동한다. 미연결이면 「미연결」로 표시된다.',
      target: 'bb-test-ben-col',
      keywords: ['수급자', '미연결', '112'],
    },
    {
      name: '우측 · 계정',
      description:
        '현재 / 추천 / 전체. 수입은 예전에 지정한 적요 제안만. 지출은 적요 제안 + 수입–지출 연결(짝 태그). 지출은 계정 탭에서 지출계정을 고르고, 상대계정 탭에서 상대계정을 고른 뒤, 탭 아래 「조정하기」로 한 번에 넣는다. 지정하면 적요가 기억되어 이후 제안에 쓰인다.',
      target: 'bb-test-account',
      keywords: ['계정', '추천', '짝', '기억'],
    },
    {
      name: '추천 기준 (톱니)',
      description:
        '적요와 계정이 어떻게 연결돼 있는지입니다. 수입/지출로 나뉘고, 이 기관에만 저장됩니다. 빈 계정만 미리본 뒤 넣습니다.',
      target: 'bb-test-recommend-settings',
      keywords: ['추천기준', '기억된적요', '참고범위', '톱니'],
    },
    {
      name: '수입–지출 연결',
      description:
        '추천 기준 톱니에서 연다. 연도별 수입→허용 지출 추가·삭제. 지출 탭 계정 추천에 짝으로 표시. 계정명은 정확히 일치해야 함.',
      target: 'bb-test-pairs',
      keywords: ['짝짓기', '허용지출', '연결'],
    },
    {
      name: '기억된 적요·계정',
      description:
        '수입/지출 탭·검색·목록에서 키워드→계정을 보고 삭제합니다. 우측에서 계정을 직접 지정하면 쌓입니다.',
      target: 'bb-test-patterns',
      keywords: ['기억', '학습', '키워드', '적요'],
    },
    {
      name: '우측 · 상대계정',
      description:
        '지출 탭에서만 보입니다. 잔액이 있는 수입 계정을 골라 둔다. 계정 탭의 지출계정과 함께 「조정하기」로 한 번에 넣는다.',
      target: 'bb-test-rel',
      keywords: ['상대계정', '잔액'],
    },
    {
      name: '우측 · 자금원천',
      description:
        '체크한 항목만 자금원천이 바뀐다. 빈칸을 한꺼번에 넣으려면 「자동화 적용」에서 다음을 눌러 고른 뒤 적용하기를 누른다.',
      target: 'bb-test-funding',
      keywords: ['자금원천', '원천', '자동화'],
    },
    {
      name: '자금원천 설정 (톱니)',
      description:
        '계정과 자금원천이 어떻게 연결돼 있는지입니다. 수입/지출로 나뉘고, 이 기관에만 저장됩니다. 빈 자금원천만 미리본 뒤 넣습니다.',
      target: 'bb-test-funding-settings',
      keywords: ['자금원천설정', '규칙', '톱니'],
    },
    {
      name: '우측 · 분개',
      description:
        '1건 선택 시 원본 금액을 여러 계정으로 나눕니다(합계=원본). 수입·지출 모두. 이미 분개된 항목은 「분개 합치기」로 원본 한 건으로 되돌리거나 「분개 수정」으로 다시 나눌 수 있다.',
      target: 'bb-test-split',
      keywords: ['분개', '분할', '합치기', '원본복원'],
    },
    {
      name: '수정하기 (적요·금액)',
      description:
        '1건 선택 시 오른쪽 「적요 수정」이 바로 펼쳐진다. 적요·금액 저장·삭제. 금액은 마이너스를 넣을 수 있고, 수입/지출은 금액 옆 버튼으로 바꾼다. 여러 건이면 계정·자금 일괄과 「선택 삭제」.',
      target: 'bb-test-quick-edit',
      keywords: ['수정하기', '적요', '금액', '삭제', '접힘'],
    },

    /* —— 2. 수납 —— */
    {
      name: '수납 목록',
      description:
        '연월·검색·청구/입금/미수 요약. 인정번호·청구 편집, 입금은 통장 매칭(읽기전용). 항목 추가/삭제, 선택·전체 삭제(통장 매칭 본인부담금은 잠금).',
      target: 'bb-test-collection',
      keywords: ['수납', '청구', '입금', '미수'],
    },
    {
      name: '수납 엑셀',
      description:
        '양식 다운로드·업로드로 수급자·청구 일괄 등록. 업로드 후 목록 즉시 갱신. 선택 통장 마감 시 비활성.',
      target: 'bb-test-collection-excel',
      keywords: ['엑셀', '수납업로드'],
    },
    {
      name: '미수금 이력',
      description: '누적미수 클릭 등으로 수급자·기간별 미수 이력을 검색·조회한다.',
      target: 'bb-test-arrears',
      keywords: ['미수', '이력'],
    },

    /* —— 3. 영수증 —— */
    {
      name: '영수증 매칭',
      description:
        '통장(미매칭/매칭/전체)·영수증·A4 캔버스 3단. 왼쪽 지출을 고르고 영수증을 눌러 오른쪽에 올린 뒤 「매칭」. 날짜가 달라도 맞출 수 있고, 그때는 저장 전에 확인. 여러 장이면 묶어서 매칭. 이미 맞춘 영수증도 연필 버튼으로 날짜·적요 수정.',
      target: 'bb-test-receipt',
      keywords: ['영수증', '매칭', '자르기', '캔버스', '합성'],
    },

    /* —— 4. 계정별내역 —— */
    {
      name: '계정별내역',
      description:
        '연월·사업구분·통장 기준 계정별 참고 조회. 여기서 원장을 직접 고치지 않는다.',
      target: 'bb-test-analysis',
      keywords: ['계정별', '분석', '참고'],
    },
  ],
  notes: [
    '마감된 통장은 더 이상 고칠 수 없어요. 고치려면 먼저 「마감 해제」를 해야 합니다.',
    '계정 추천은 AI가 아니에요. 예전에 직접 지정했던 적요·계정을 기억해 뒀다가 보여 줄 뿐이에요.',
    '통장에서 고른 항목만 넣으려면 그 항목을 선택한 뒤 「자동화 적용」을 누르세요. 고르지 않으면 이번 달 전체에 적용됩니다.',
    '수입을 고르면 추천에 수입 계정만 나와요. 수입과 묶인 지출 계정은, 지출 항목을 골랐을 때 추천에 나와요.',
  ],
};

export function bankbookTestFeatureSpecOf(
  routeName: string | symbol | null | undefined
): BankbookTestFeatureSpec | null {
  const name = String(routeName || '');
  // (구) 테스트 라우트 이름은 리다이렉트용으로만 남아 있고, 실제 화면은 memberBankbook 계열
  if (
    name === 'memberBankbook' ||
    name === 'memberDetailBankbook' ||
    name === 'memberBankbookTest' ||
    name === 'memberDetailBankbookTest'
  ) {
    return BANKBOOK_TEST_FEATURE_SPEC;
  }
  return null;
}
